import ModalBasic from "@shared/components/ModalBasic";
import { useEffect, useRef, useState } from "react";
import { ConfirmationModal } from "../../../shared/components/ConfirmationModal";
import { TestLine } from "../types/test-detail.types";

interface LineEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  lines: TestLine[];
  startLineNumber: number;
  onSave: (lines: TestLine[]) => void;
}

export function LineEditorModal({
  isOpen,
  onClose,
  lines,
  startLineNumber,
  onSave,
}: LineEditorModalProps) {
  // Convert lines to a single text string with line numbers
  const initialText = lines.map((line) => line.text).join("\n");
  const [editorText, setEditorText] = useState(initialText);
  const [originalText] = useState(initialText);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Update the text when lines change
  useEffect(() => {
    const newText = lines.map((line) => line.text).join("\n");
    setEditorText(newText);
  }, [lines]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 5}px`;
    }
  }, [editorText]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return editorText !== originalText;
  };

  // Handle modal close with confirmation if needed
  const handleCloseWithConfirmation = () => {
    if (hasUnsavedChanges()) {
      setShowConfirmation(true);
    } else {
      onClose();
    }
  };

  // Handle save
  const handleSave = () => {
    // Split the text into lines
    const textLines = editorText.split("\n");

    // Create new line objects with sequential numbers
    const newLines: TestLine[] = textLines.map((text, index) => ({
      number: startLineNumber + index,
      text,
    }));

    onSave(newLines);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalBasic
        id="line-editor-modal"
        title="Edit Passage Lines"
        modalOpen={isOpen}
        setModalOpen={handleCloseWithConfirmation}
      >
        <div className="bg-white dark:bg-slate-800 rounded shadow-lg w-full">
          <div className="p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Edit all lines at once. Each line will be automatically numbered.
              </p>
            </div>

            <div className="flex">
              {/* Line numbers column */}
              <div className="w-12 pr-2 pt-2 flex-shrink-0 bg-gray-50 dark:bg-gray-700 rounded-l-md">
                {editorText.split("\n").map((_, index) => (
                  <div
                    key={index}
                    className="text-right pr-2 text-xs text-gray-500 dark:text-gray-400 leading-relaxed"
                    style={{ lineHeight: "1.5rem" }}
                  >
                    {startLineNumber + index}
                  </div>
                ))}
              </div>

              {/* Textarea */}
              <div className="flex-grow">
                <textarea
                  ref={textareaRef}
                  value={editorText}
                  onChange={(e) => setEditorText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-r-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter passage text line by line"
                  rows={Math.max(5, editorText.split("\n").length)}
                  style={{ lineHeight: "1.5rem", resize: "none" }}
                />
              </div>
            </div>
          </div>

          {/* Modal footer */}
          <div className="sticky bottom-0 px-5 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700/60">
            <div className="flex flex-wrap justify-end space-x-2">
              <button
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={handleCloseWithConfirmation}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                onClick={handleSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </ModalBasic>

      {showConfirmation && (
        <ConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={() => {
            setShowConfirmation(false);
            onClose();
          }}
          title="Discard Changes"
          message="You have unsaved changes. Are you sure you want to discard them?"
          confirmText="Discard"
          cancelText="Keep Editing"
        />
      )}
    </>
  );
}
